import React, { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { messageObject } from './messageData';
import Modal from '../../../elements/Modal';
import { useParams } from 'react-router-dom';
import { abortBuild, abortWorkflow, checkBuildStatusGithub, checkBuildStatusJenkins, triggerGithubWorkflow, triggerJenkinsJob, updateIterationWorkflow } from '../../../../modules/automation';
import { useNotification } from '../../../../hooks/NotificationContext';
import { getCurrentProject } from '../../../../modules/project';

const RunAutomationModal = ({
  show,
  handleClose,
  testCycleId,
  activeTab,
  executions,
  testCycleData,
}) => {
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState('');
  const [disableConfirm, setDisableConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [automationMethod, setAutomationMethod] = useState('');
  const { addNotification } = useNotification();
  const [runId, setRunId] = useState(null);
  const jobName = testCycleData?.job;
  const testcases =
    executions?.map((exec) => exec?.association?.testcase?.key) || [];
  const testcaseKeys = testcases.join(',');
  const { projectId } = useParams();

  const fetchCurrentProject = async () => {
    try {
      const response = await getCurrentProject(projectId);
      setAutomationMethod(response?.automation_method);
    } catch (error) {
      const errorMessage = error.message || 'Failed to fetch automation method';
      addNotification('danger', errorMessage);
    }
  };

  useEffect(() => {
    fetchCurrentProject();
  }, []);

  const triggerJenkinsBuild = async () => {
    try {
      setMessage('TRIGGER');
      await triggerJenkinsJob({
        testcycle: testCycleData?.key,
        iteration: activeTab,
        testcaseKeys,
        jobName,
        projectId
      })
      handleConfirmClose();
      addNotification('success', `Jenkins job successfully triggered.`);
    } catch (error) {
      const errorMessage = error.message || 'Failed to trigger Jenkins job';
      handleConfirmClose();
      addNotification('danger', errorMessage);
    }
  };

  const triggerGithub = async () => {
    try {
      setMessage('triggering');
      const response = await triggerGithubWorkflow({
        testcycle: testCycleData?.key,
        iteration: activeTab,
        testcaseKeys,
        jobName,
        projectId
      })
      if (response?.workflow_id) {
        setTimeout(async () => {
          await updateIterationWorkflow({ iteration: activeTab, workflowId: response?.workflow_id, projectId });
          handleConfirmClose();
          addNotification('success', `Github Workflow successfully triggered.`);
        }, 1500);
      }
    } catch (error) {
      const errorMessage = error.message || 'Failed to run the automation';
      handleConfirmClose();
      addNotification('danger', errorMessage);
    }
  };

  const handleRunAutomation = () => {
    setDisableConfirm(true);
    setIsSubmitting(true);
    if (automationMethod === 'jenkins') {
      triggerJenkinsBuild(testcaseKeys);
    } else if (automationMethod === 'github') {
      triggerGithub(testcaseKeys);
    }
  };

  const checkData = async () => {
    setLoading(true);
    try {
      const response =
        automationMethod === 'github'
          ? await checkBuildStatusGithub(testCycleId, activeTab, jobName, projectId)
          : await checkBuildStatusJenkins(testCycleId, activeTab, jobName, projectId)
      if (automationMethod === 'github') {
        const { conclusion, status, id } = response?.data;
        setRunId(id);
        setLoading(false);
        if (conclusion === 'cancelled') {
          setActionType('run');
          setMessage('cancelled');
        } else if (
          status === 'completed' ||
          status === 'failure' ||
          status === 'NO_BUILD'
        ) {
          setActionType('run');
          setMessage(status);
        } else if (status === 'in_progress') {
          setActionType('abort');
          setMessage(status);
        } else {
          setActionType('');
          setMessage(status);
        }
      } else {
        const { result, building } = response?.data;
        setLoading(false);
        if (building) {
          setActionType('abort');
          setMessage('BUILDING');
        } else if (
          result === 'SUCCESS' ||
          result === 'ABORTED' ||
          result === 'FAILURE' ||
          result === 'NO_BUILD'
        ) {
          setActionType('run');
          setMessage(result);
        } else if (result === 'METHOD_CHANGE') {
          setActionType('');
          setMessage(result);

        }
      }
    } catch (error) {
      setError(error.message);
      setLoading(false)
    }
  };
  useEffect(() => {
    if (show && testCycleId && activeTab && automationMethod) {
      checkData();
    }
  }, [show]);

  const handleConfirmClose = () => {
    handleClose();
    setActionType('');
    setMessage('');
    setDisableConfirm(false);
    setIsSubmitting(false);
    setError('');
  };
  const handleAbortBuild = async () => {
    try {
      let response = null;
      if (automationMethod === 'github' && runId) {
        response = await abortWorkflow({ iteration: activeTab, runId, projectId })
        addNotification('success', response.message);
        handleConfirmClose();
      } else {
        response = await abortBuild({ testCycleId, iteration: activeTab, jobName, projectId });
        addNotification('success', response.message);
        handleConfirmClose();
      }
    } catch (error) {
      addNotification('danger', error.message);
    }
  };

  return (
    <Modal
      open={show}
      handleClose={handleConfirmClose}
      modalTitle='Automation'
      action={
        () => (
          <>
            {actionType === 'abort' ? (
              <Button variant="danger" onClick={handleAbortBuild} title="Abort">
                Abort
              </Button>
            ) : actionType === 'run' ? (
              <Button
                variant="primary"
                onClick={handleRunAutomation}
                disabled={disableConfirm || isSubmitting}
                title="Run Automation">
                {isSubmitting ? 'Running...' : 'Run'}
              </Button>
            ) : null}
            {!isSubmitting && (
              <Button
                variant="outline-secondary"
                onClick={handleConfirmClose}
                title="Cancel">
                Cancel
              </Button>
            )}
          </>
        )
      }
    >
      <>
        {loading ? (
          <p className="text-center"> Checking previous build status...</p>
        ) : (
          <p className="mb-2">{error || messageObject[message]}</p>
        )}
      </>

    </Modal>
  );
};

export default RunAutomationModal;
